import type { Language } from 'services/i18n/data';
import { useTranslation } from '@gympass/i18n';
import { useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { AppRouterContext } from 'providers/AppRouter';
import { useRootContext } from './useRootContext';

/**
 * Hook that syncs app state with i18n language changes.
 *
 * When the i18n language changes, this hook:
 * - reflects the locale on `<html lang>`, so language-aware vendors (e.g. the
 *   OneTrust banner) and assistive tech track it (the initial value is set
 *   synchronously at bootstrap in App.tsx);
 * - invalidates the router to re-evaluate head() functions, ensuring
 *   translated titles and meta tags are updated.
 *
 * Call this once in your root component (__root.tsx).
 *
 * @see https://github.com/TanStack/router/discussions/3666
 */
export function useRouterSyncLanguage() {
  const router = useRouter();
  const { i18n } = useTranslation();
  const context = useRootContext();

  useEffect(() => {
    const handler = () => {
      document.documentElement.lang = i18n.language;

      // Update router context with new language
      router.update({
        context: {
          ...context,
          localization: {
            ...context.localization,
            language: i18n.language as Language,
          },
        } as AppRouterContext,
      });
      router.invalidate();
    };

    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, [router, i18n, context]);
}
